import type { LegalCitation } from '@buakei/types';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

type InspectorValue = {
  citation: LegalCitation | null;
  open: (citation: LegalCitation, trigger?: HTMLElement | null) => void;
  close: () => void;
};

const InspectorContext = createContext<InspectorValue | null>(null);

/**
 * Citations open in an inspector that preserves page context (DESIGN-SYSTEM rule 9).
 * Focus returns to the citation that opened it.
 */
export function InspectorProvider({ children }: { children: ReactNode }) {
  const [citation, setCitation] = useState<LegalCitation | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  const open = useCallback((next: LegalCitation, trigger?: HTMLElement | null) => {
    triggerRef.current = trigger ?? null;
    setCitation(next);
  }, []);

  const close = useCallback(() => {
    setCitation(null);
    const trigger = triggerRef.current;
    if (trigger) requestAnimationFrame(() => trigger.focus());
  }, []);

  const value = useMemo(() => ({ citation, open, close }), [citation, open, close]);
  return <InspectorContext.Provider value={value}>{children}</InspectorContext.Provider>;
}

export function useInspector(): InspectorValue {
  const value = useContext(InspectorContext);
  if (!value) throw new Error('useInspector must be used inside InspectorProvider');
  return value;
}
